import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBaskets } from '../context/BasketContext';

export default function BasketsPage() {
  const { isAuthenticated } = useAuth();
  const { userBaskets, systemBaskets, currencySymbol, loading, createBasket, deleteBasket } = useBaskets();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  if (!isAuthenticated) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Basket of Goods</h1>
        <p className="text-gray-500 mb-4">Log in to create and manage your grocery baskets.</p>
        <Link to="/login" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition">
          Log In
        </Link>
      </div>
    );
  }

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    const basket = await createBasket(newName.trim(), newDesc.trim());
    if (basket) {
      setNewName('');
      setNewDesc('');
      setShowCreateForm(false);
      navigate(`/baskets/${basket.id}`);
    }
    setCreating(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    await deleteBasket(id);
  };

  const BasketCard = ({ basket, isSystem }) => (
    <Link
      to={`/baskets/${basket.id}`}
      className="block bg-white rounded-xl shadow-md hover:shadow-lg transition p-5 border border-gray-100"
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-800 truncate">{basket.name}</h3>
            {isSystem && (
              <span className="shrink-0 text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                Featured
              </span>
            )}
          </div>
          {basket.description && (
            <p className="text-sm text-gray-500 line-clamp-2 mb-2">{basket.description}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>{basket.items_count || 0} items</span>
            {basket.cheapest_total && (
              <span className="text-green-600 font-medium">
                From {currencySymbol}{parseFloat(basket.cheapest_total).toFixed(2)}
                {basket.cheapest_store && ` at ${basket.cheapest_store}`}
              </span>
            )}
          </div>
        </div>
        {!isSystem && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(basket.id, basket.name); }}
            className="ml-2 text-gray-300 hover:text-red-500 transition p-1"
            title="Delete basket"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    </Link>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Basket of Goods</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Basket
        </button>
      </div>

      {showCreateForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-md p-5 mb-6 border border-blue-100">
          <h3 className="font-semibold text-gray-700 mb-3">Create New Basket</h3>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Basket name (e.g. Weekly Groceries)"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
            required
            autoFocus
          />
          <textarea
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Optional description"
            rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowCreateForm(false)} className="text-sm text-gray-500 px-3 py-1.5 hover:text-gray-700">
              Cancel
            </button>
            <button type="submit" disabled={creating} className="bg-blue-600 text-white text-sm font-semibold px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition">
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Loading baskets...</p>
        </div>
      ) : (
        <>
          {systemBaskets.length > 0 && (
            <div className="mb-8">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Featured Baskets</h2>
              <div className="grid gap-3">
                {systemBaskets.map(b => <BasketCard key={b.id} basket={b} isSystem />)}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">My Baskets</h2>
            {userBaskets.length > 0 ? (
              <div className="grid gap-3">
                {userBaskets.map(b => <BasketCard key={b.id} basket={b} isSystem={false} />)}
              </div>
            ) : (
              <div className="text-center py-10 bg-gray-50 rounded-xl">
                <p className="text-gray-500 mb-2">You haven't created any baskets yet.</p>
                <p className="text-sm text-gray-400">Create one to start tracking prices on your regular groceries.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
